import React from "react";
import MobileMenuButton from "../Sidebar/MobileMenuButton";
import { useHeaderSlot } from "../Sidebar/MobileNavContext";
import { cn } from "../../lib/utils";

/**
 * Cabecera de sección: título, bajada y la acción principal.
 *
 * En móvil incorpora la hamburguesa que abre el menú. Va aquí y no flotando
 * sobre el contenido porque es donde el diseño la pone —en la misma línea que
 * el título— y porque una cabecera es lo único que toda pantalla tiene en el
 * mismo sitio. El shell sólo pinta la suya cuando no hay ninguna cabecera
 * montada; `useHeaderSlot` es lo que se lo dice.
 *
 * **Sin título no reclama el hueco.** Una cabecera sin título no es la cabecera
 * de la pantalla: es una tira de controles embebida dentro de una sección que
 * ya se nombra a sí misma —Usage dentro de Activity, por ejemplo—. Si reclamara,
 * la hamburguesa aparecería a media página, debajo del título de verdad.
 *
 * El armazón es una rejilla de tres columnas y dos filas, y no dos flex
 * anidados, por un detalle: la bajada tiene que ir a lo ancho **debajo de
 * todo** en móvil y **debajo del título nada más** en escritorio, con la
 * acción alineada al pie del bloque entero. Con flex eso pedía renderizar la
 * acción dos veces, una por breakpoint.
 */
export function PageHeader({ title, subtitle, action, className }) {
  const isPrimaryHeader = useHeaderSlot(Boolean(title));

  return (
    <div
      className={cn(
        "grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-y-1.5",
        className
      )}
    >
      {/* `mr-3` en vez de `gap-x`: desplegado el botón no existe, y un hueco
          entre columnas dejaría el título indentado en escritorio. */}
      {isPrimaryHeader ? (
        <MobileMenuButton className="col-start-1 row-start-1 mr-3" />
      ) : (
        <div className="col-start-1 row-start-1" />
      )}

      {/* Sin titulo el bloque desaparece pero la accion se queda: quien
          embebe esta cabecera dentro de una seccion que ya lo nombra no
          necesita repetirlo, pero si necesita sus controles. */}
      {title ? (
        <h1 className="col-start-2 row-start-1 min-w-0 text-2xl font-semibold tracking-tight text-white sm:text-3xl md:text-4xl">
          {title}
        </h1>
      ) : (
        <div className="col-start-2 row-start-1" />
      )}

      {action && (
        <div className="col-start-3 row-start-1 ml-3 shrink-0 self-center md:row-span-2 md:self-end">
          {action}
        </div>
      )}

      {subtitle && (
        <p className="col-span-3 col-start-1 row-start-2 text-sm text-gray-300 md:col-span-1 md:col-start-2">
          {subtitle}
        </p>
      )}
    </div>
  );
}

export default PageHeader;
